import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import { filter } from "../../common"
import { useScatterPlotGraphOption } from "./options"
import { DownLoadData, DownLoadHeader, DownLoadRecord } from "components/download/csv/types"

export type PlotData = [
  number, // x
  number, // y
  number | undefined, // size
  string | undefined, // id
  string | undefined, // name
  string | undefined  // series
]
export const parsePlotData = (data: PlotData) => ({
  x: data[0],
  y: data[1],
  size: data[2],
  id: data[3],
  name: data[4],
  series: data[5]
})

type useScatterPlotGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useScatterPlotGraphOption<T>>
}
export const useScatterPlotGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    xValueDef, yValueDef, sizeDef, colorDef
  }
}: useScatterPlotGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!xValueDef?.key || !yValueDef?.key) return null

		//console.log('build scatter graph data')

		const identifierFunc = (data: T) => {
			if (configure.identifier) return configure.identifier(data)
			return undefined
		}
		const stackFunc = (data: T) => {
			if (colorDef) return colorDef.val(data)
			if (configure.stack) return configure.stack(data)
			return undefined
		}
		let xmin = xValueDef.minValue === undefined ? null : xValueDef.minValue
		let xmax = xValueDef.maxValue === undefined ? null : xValueDef.maxValue
		let ymin = yValueDef.minValue === undefined ? null : yValueDef.minValue
		let ymax = yValueDef.maxValue === undefined ? null : yValueDef.maxValue
		let totalSize = 0
		let maxSize = undefined
		let minSize = undefined

		const series: {
			code: string
			name?: string
			data: PlotData[]
		}[] = []
		for (const record of dataList) {
			const xValue = xValueDef.val(record)
			if (xValue === undefined) continue
			const yValue = yValueDef.val(record)
			if (yValue === undefined) continue

			const identifier = identifierFunc(record)

			xmin = xmin === null ? xValue : Math.min(xValue, xmin)
			xmax = xmax === null ? xValue : Math.max(xValue, xmax)
			ymin = ymin === null ? yValue : Math.min(yValue, ymin)
			ymax = ymax === null ? yValue : Math.max(yValue, ymax)

			let filtered = filter(record, configure, conditions, "identifier", targetList) ? false : true
			if (!filtered && filterText && identifier?.name) {
				if (filterText.split(' ').every((t) => identifier.name.indexOf(t) < 0)) filtered = true
			}
			const size = filtered ? undefined : sizeDef ? sizeDef.val(record) : 1
			const stack = stackFunc(record)
			const stacks = stack ?? { code: '***', name: undefined }
			for (const stack of Array.isArray(stacks) ? stacks : [stacks]) {
				let its = series.find((s) => s.code === stack.code)
				if (!its) {
					its = {
						code: stack.code,
						name: stack.name,
						data: []
					}
					series.push(its)
				}
				its.data.push([
					xValue, yValue, size ?? 0,
					identifier?.id,
					identifier?.name,
					stack.name
				])
				if (size !== undefined) {
					maxSize = (maxSize === undefined) ? size : Math.max(maxSize, size)
					minSize = (minSize === undefined) ? size : Math.min(minSize, size)
					totalSize = totalSize + size
				}
			}
		}
		series.sort((a, b) => {
			if (a.code === b.code) return 0
			if (a.code === '***') return 1
			if (b.code === '***') return -1
			return a.code.localeCompare(b.code)
		})
		return {
			xmin, xmax, ymin, ymax,
			maxSize, minSize, totalSize,
			series,
			csvBuilder: (): DownLoadData | undefined => {
				if (!xValueDef?.key || !yValueDef?.key) return undefined

				const xValueKey = xValueDef.key
				const yValueKey = yValueDef.key
				const sizeKey = sizeDef?.key
				const colorKey = colorDef?.key
				const header: DownLoadHeader[] = [
					{ title: '名前', key: 'name' },
					{ title: xValueDef.title, key: xValueDef.key, unit: xValueDef.unit },
					{ title: yValueDef.title, key: yValueDef.key, unit: yValueDef.unit },
					sizeDef ? { title: sizeDef.title, key: sizeDef.key } : [],
					colorDef ? { title: colorDef.title, key: colorDef.key } : []
				].flat()
	
				const flatSeriesData = series.flatMap((series) => series.data.map(parsePlotData))
				for (let index = flatSeriesData.length - 1; index > 0; index--) {
					const ramdomIndex = Math.floor(Math.random() * (index + 1))
					;[flatSeriesData[index], flatSeriesData[ramdomIndex]] = [
						flatSeriesData[ramdomIndex],
						flatSeriesData[index],
					]
				}
				const body: DownLoadRecord[] = flatSeriesData.flatMap((data) => ({
					name: data.name,
					[xValueKey]: data.x,
					[yValueKey]: data.y,
					[sizeKey ?? 2]: data.size,
					[colorKey ?? 3]: data.series
				}))
				.slice(0, 1000)
	
				return { header, body }
			}
		}
	},
	[dataList, xValueDef, yValueDef, sizeDef, colorDef, filterText, conditions, configure, targetList]
)
